import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, AlertTriangle, Package } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { insertApplicationSchema } from "@shared/schema";

// Create a client-side schema that excludes tenantId since it's added by the backend
const clientApplicationSchema = insertApplicationSchema.omit({ tenantId: true });
import { apiRequest } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAppSettings } from "@/hooks/use-app-settings";

type AddApplicationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function AddApplicationDialog({ open, onOpenChange }: AddApplicationDialogProps) {
  const { toast } = useToast();
  const { getSettingsByType } = useAppSettings();
  const allCategorySettings = getSettingsByType("category");
  // Filter active, exclude "Other" since it's always added as a hardcoded option
  const categorySettings = allCategorySettings.filter(
    setting => setting.isActive && setting.value.toLowerCase() !== "other"
  );
  const [customCategory, setCustomCategory] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [potentialDuplicates, setPotentialDuplicates] = useState<any[]>([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  
  const form = useForm({
    resolver: zodResolver(clientApplicationSchema),
    defaultValues: {
      name: "",
      description: "",
      category: ""
    }
  });
  
  // Get all applications for duplicate checking (optimized)
  const { data: rawApplications } = useQuery({
    queryKey: ['/api/applications'],
    queryFn: async () => {
      const response = await fetch("/api/applications?limit=50");
      if (!response.ok) throw new Error('Failed to fetch applications');
      return response.json();
    },
    enabled: open,
    staleTime: 30000,
    refetchOnWindowFocus: false
  });
  const applications = Array.isArray(rawApplications) ? rawApplications : [];
  
  // Watch for name changes
  const watchName = form.watch("name");
  
  // Check for duplicates when name changes (optimized for performance)
  useEffect(() => {
    if (!applications || !watchName || watchName.length < 3) {
      setPotentialDuplicates([]);
      return;
    }
    
    const checkTimer = setTimeout(() => {
      // Use requestIdleCallback for non-blocking execution
      const performCheck = () => {
        setIsCheckingDuplicates(true);
        
        try {
          // Normalize strings for comparison (faster version)
          const normalize = (str: string) => {
            return str?.toLowerCase().replace(/\s+/g, '').substring(0, 20) || '';
          };
          
          const normalizedName = normalize(watchName);
          
          // Find potential duplicates with early exit for performance
          const duplicates = applications.filter((app: any) => {
            const appName = normalize(app.name);
            
            // Quick similarity check with early exit
            return normalizedName.includes(appName) || appName.includes(normalizedName);
          }).slice(0, 3); // Limit results to prevent UI slowdown
          
          setPotentialDuplicates(duplicates);
        } catch (error) {
          console.error("Error checking for duplicates:", error);
        } finally {
          setIsCheckingDuplicates(false);
        }
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(performCheck);
      } else {
        setTimeout(performCheck, 0);
      }
    }, 800); // Increased debounce to reduce frequent checks
    
    return () => clearTimeout(checkTimer);
  }, [watchName, applications]);
  
  // Mutation to create new category
  const createCategoryMutation = useMutation({
    mutationFn: async (categoryName: string) => {
      const response = await fetch("/api/app-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "category",
          value: categoryName,
          isActive: true
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to create category");
      }
      return response.json();
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      // If using custom category, create it first
      if (selectedCategory === "Other" && customCategory.trim()) {
        await createCategoryMutation.mutateAsync(customCategory.trim());
        data.category = customCategory.trim();
      }
      
      const response = await fetch("/api/applications", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to add application");
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/applications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/app-settings'] });
      queryClient.invalidateQueries({ queryKey: ['/api/subscription-status'] });
      toast({
        title: "Application added",
        description: "The application has been added successfully.",
      });
      form.reset();
      setPotentialDuplicates([]);
      setSelectedCategory("");
      setCustomCategory("");
      onOpenChange(false);
    },
    onError: (error: any) => {
      // Handle specific validation and duplicate name errors
      let errorMessage = error.message;
      
      if (error.message.includes('already exists') || error.message.includes('Application name already exists')) {
        errorMessage = "An application with this name already exists in your organization. Please choose a different name.";
      } else if (error.message.includes('Validation failed')) {
        errorMessage = "Please check all required fields and ensure they are properly filled.";
      } else if (error.message.includes('whitespace')) {
        errorMessage = "Application name cannot be only whitespace. Please enter a valid name.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  function onSubmit(data: any) {
    mutation.mutate(data);
  }

  const resetDialogState = () => {
    form.reset({
      name: "",
      description: "",
      category: ""
    });
    setCustomCategory("");
    setSelectedCategory("");
    setPotentialDuplicates([]);
    setIsCheckingDuplicates(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetDialogState();
    }

    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Application</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Application Name*</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter application name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter a brief description of the application" 
                      className="resize-none"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category*</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      setSelectedCategory(value);
                      if (value !== "Other") {
                        field.onChange(value);
                      }
                    }} 
                    value={selectedCategory || field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categorySettings.map((setting) => (
                        <SelectItem key={setting.id} value={setting.value}>
                          {setting.value}
                        </SelectItem>
                      ))}
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                  {selectedCategory === "Other" && (
                    <div className="mt-2">
                      <Input
                        placeholder="Enter custom category"
                        value={customCategory}
                        onChange={(e) => {
                          setCustomCategory(e.target.value);
                          field.onChange(e.target.value);
                        }}
                      />
                    </div>
                  )}
                </FormItem>
              )}
            />
            
            
            {/* Display potential duplicates warning */}
            {potentialDuplicates.length > 0 && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Potential Duplicate Applications</AlertTitle>
                <AlertDescription className="mt-2">
                  <p className="mb-2 text-sm text-amber-700">
                    We found similar applications that may already exist in your system:
                  </p>
                  <div className="max-h-32 overflow-y-auto border rounded border-amber-200 bg-white">
                    {potentialDuplicates.map((app) => (
                      <div key={app.id} className="flex items-center p-2 border-b border-amber-100">
                        <div className="mr-2 w-6 h-6 rounded bg-amber-100 flex items-center justify-center">
                          <Package className="h-3 w-3 text-amber-700" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{app.name}</p>
                          <p className="text-xs text-slate-500">{app.category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-amber-700">
                    Consider updating existing applications instead of creating duplicates.
                  </p>
                </AlertDescription>
              </Alert>
            )}
            
            
            <DialogFooter className="pt-4">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Adding...
                  </>
                ) : (
                  'Add Application'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
